import React from 'react';
import { Chip, Tooltip, Box, Typography } from '@mui/material';

/**
 * Displays an entity by name with code as tooltip; falls back to code-only
 * when name is missing. Optional onClick navigates to a detail page.
 *
 * Use this everywhere we currently show a bare code Chip (warehouse, supplier,
 * brand, SKU). One pattern, one source of truth.
 *
 * Variants:
 *   chip (default)  — compact pill suitable for table cells
 *   stacked         — primary name on top, code+meta in monospace below
 */
export function EntityChip({
  code, name, subtitle, onClick,
  variant = 'chip',           // 'chip' | 'stacked'
  chipVariant = 'outlined',   // forwarded to MUI Chip when variant='chip'
  size = 'small',
}) {
  if (!code && !name) return null;
  const showName = Boolean(name);
  const label = name || code;
  const tooltipText = showName
    ? `${code}${subtitle ? ` · ${subtitle}` : ''}`
    : (subtitle || null);

  if (variant === 'stacked') {
    return (
      <Box sx={{ display: 'inline-flex', flexDirection: 'column', minWidth: 0, cursor: onClick ? 'pointer' : 'default' }} onClick={onClick}>
        <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {label}
        </Typography>
        {showName && (
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: 10, lineHeight: 1.2 }}>
            {code}{subtitle ? ` · ${subtitle}` : ''}
          </Typography>
        )}
        {!showName && subtitle && (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, lineHeight: 1.2 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    );
  }

  const chip = (
    <Chip
      label={label}
      size={size}
      variant={chipVariant}
      onClick={onClick}
      clickable={Boolean(onClick)}
      sx={{ maxWidth: 240, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
    />
  );
  return tooltipText ? <Tooltip title={tooltipText} arrow>{chip}</Tooltip> : chip;
}

export default EntityChip;
